import { useState, type CSSProperties } from 'react';
import { useList, useUpdate } from '@refinedev/core';
import { GripVertical, Plus, Trash2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SortableList } from '@/components/dnd';
import { FormCard, FormField, FormStickyActions, FormSpacer } from '@/components/forms';
import { MultiCurrencyPrices, type MultiCurrencyPricesValue } from '@/components/forms/MultiCurrencyPrices';
import {
  LanguageSwitcher,
  type TranslationLanguage,
  type LanguageCode,
  LANGUAGE_CODES,
} from '@/components/translations';
import type { Course, CourseFormData } from '../types';

// ─── Types ──────────────────────────────────────────

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  flagEmoji?: string;
}

interface AccommodationOption {
  id: string;
  /** @deprecated — используй translations */
  name?: string;
  translations: Record<string, string>;
  prices: Record<string, number>;
  baseCurrency: string;
}

// ─── Component ──────────────────────────────────────

interface CourseMeetingTabProps {
  course: Course;
  onUpdated: () => void;
}

export function CourseMeetingTab({ course, onUpdated }: CourseMeetingTabProps) {
  const { mutateAsync: update, mutation } = useUpdate();

  // Load languages
  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code, name: l.name, nativeName: l.nativeName, flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const existingOptions = extractAccommodationOptions(course.prices);

  const [capacity, setCapacity] = useState<number | null>(course.capacity ?? null);

  const coursePrices = course.prices as Record<string, number> | null;
  const [pricing, setPricing] = useState<MultiCurrencyPricesValue>({
    price: course.price ?? null,
    currency: course.baseCurrency ?? course.currency ?? 'UZS',
    prices: coursePrices && typeof coursePrices === 'object' && !Array.isArray(coursePrices)
      ? filterNumericPrices(coursePrices)
      : null,
    baseCurrency: course.baseCurrency ?? course.currency ?? 'UZS',
  });

  const [accommodations, setAccommodations] = useState<AccommodationOption[]>(existingOptions);

  const addAccommodation = () => {
    const newOption: AccommodationOption = {
      id: crypto.randomUUID(),
      translations: {},
      prices: {},
      baseCurrency: pricing.baseCurrency,
    };
    setAccommodations([...accommodations, newOption]);
  };

  const removeAccommodation = (id: string) => {
    setAccommodations(accommodations.filter((a) => a.id !== id));
  };

  const updateAccommodationTranslation = (id: string, langCode: string, value: string) => {
    setAccommodations(accommodations.map((a) =>
      a.id === id ? { ...a, translations: { ...a.translations, [langCode]: value } } : a,
    ));
  };

  const updateAccommodationPricing = (id: string, value: MultiCurrencyPricesValue) => {
    setAccommodations(accommodations.map((a) =>
      a.id === id ? { ...a, prices: value.prices ?? {}, baseCurrency: value.baseCurrency } : a,
    ));
  };

  const handleSave = async () => {
    // Validate: at least one translation filled
    const validAccommodations = accommodations.filter((a) =>
      Object.values(a.translations).some((v) => v.trim() !== ''),
    );

    const prices: Record<string, unknown> = {
      ...(pricing.prices ?? {}),
      accommodationOptions: validAccommodations.length > 0
        ? validAccommodations.map((a) => ({
            id: a.id,
            translations: a.translations,
            prices: a.prices,
            baseCurrency: a.baseCurrency,
          }))
        : undefined,
    };

    const data: Partial<CourseFormData> = {
      capacity,
      price: pricing.price,
      currency: pricing.baseCurrency,
      baseCurrency: pricing.baseCurrency,
      prices: Object.keys(prices).length > 0 ? prices : null,
    };

    await update({
      resource: 'courses',
      id: course.id,
      values: data,
    });

    onUpdated();
  };

  const saving = mutation.isPending;

  return (
    <div className="space-y-6 pt-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Основные настройки */}
        <FormCard title="Основные настройки" description="Цена и вместимость встречи">
          <div className="space-y-4">
            <MultiCurrencyPrices
              value={pricing}
              onChange={setPricing}
              label="Базовая цена"
              required
            />

            <FormField
              label="Вместимость"
              name="capacity"
              hint="Максимальное количество участников. Оставьте пустым для неограниченной вместимости."
            >
              <Input
                type="number"
                min={1}
                value={capacity ?? ''}
                onChange={(e) => setCapacity(e.target.value ? parseInt(e.target.value, 10) : null)}
                placeholder="Без ограничений"
              />
            </FormField>

            {course.bookedSeats !== null && course.bookedSeats !== undefined && course.bookedSeats > 0 && (
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                Забронировано мест: <span className="font-medium text-foreground">{course.bookedSeats}</span>
                {capacity ? ` из ${capacity}` : ''}
              </div>
            )}
          </div>
        </FormCard>

        {/* Варианты проживания */}
        <FormCard
          title="Варианты проживания"
          description="Названия локализуются. Показывается при бронировании."
        >
          <div className="space-y-4">
            {accommodations.length > 0 && (
              <SortableList
                items={accommodations}
                onReorder={setAccommodations}
                renderItem={({ item: opt, dragHandleProps }) => (
                  <AccommodationCard
                    option={opt}
                    languages={languages}
                    dragHandleProps={dragHandleProps}
                    onTranslationChange={(lang, val) => updateAccommodationTranslation(opt.id, lang, val)}
                    onPricingChange={(value) => updateAccommodationPricing(opt.id, value)}
                    onRemove={() => removeAccommodation(opt.id)}
                  />
                )}
              />
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAccommodation}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Добавить вариант
            </Button>

            {accommodations.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Нет вариантов проживания. Секция проживания не будет показана при бронировании.
              </p>
            )}
          </div>
        </FormCard>
      </div>

      <FormSpacer />
      <form onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
        <FormStickyActions>
          <Button type="submit" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </FormStickyActions>
      </form>
    </div>
  );
}

// ─── Accommodation Card ──────────────────────────────

interface AccommodationDragHandleProps {
  ref: (node: HTMLElement | null) => void;
  style: CSSProperties;
}

function AccommodationCard({
  option,
  languages,
  dragHandleProps,
  onTranslationChange,
  onPricingChange,
  onRemove,
}: {
  option: AccommodationOption;
  languages: TranslationLanguage[];
  dragHandleProps?: AccommodationDragHandleProps;
  onTranslationChange: (langCode: string, value: string) => void;
  onPricingChange: (value: MultiCurrencyPricesValue) => void;
  onRemove: () => void;
}) {
  const [activeLang, setActiveLang] = useState<LanguageCode>('ru');

  const pricingValue: MultiCurrencyPricesValue = {
    price: option.prices[option.baseCurrency] ?? null,
    currency: option.baseCurrency,
    prices: Object.keys(option.prices).length > 0 ? option.prices : null,
    baseCurrency: option.baseCurrency,
  };

  // Build translation data for LanguageSwitcher status indicator
  const translationData: Record<string, Record<string, string>> = {};
  for (const code of LANGUAGE_CODES) {
    translationData[code] = { name: option.translations[code] ?? '' };
  }

  const nameFields = [{ name: 'name', label: 'Название', required: true, type: 'input' as const }];

  return (
    <div className="flex gap-3 rounded-lg border bg-background p-3">
      <div {...(dragHandleProps ?? {})} className="mt-1 text-muted-foreground">
        <GripVertical className="h-4 w-4" />
      </div>

      <div className="flex-1 space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-2">
            {languages.length > 1 && (
              <LanguageSwitcher
                languages={languages}
                activeLanguage={activeLang}
                onLanguageChange={(code) => setActiveLang(code as LanguageCode)}
                fields={nameFields}
                value={translationData}
                variant="inline"
              />
            )}
            <Input
              value={option.translations[activeLang] ?? ''}
              onChange={(e) => onTranslationChange(activeLang, e.target.value)}
              placeholder={`Название (${activeLang.toUpperCase()})`}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="mt-1 shrink-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <MultiCurrencyPrices
          value={pricingValue}
          onChange={onPricingChange}
          label="Цена проживания"
        />
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────

function filterNumericPrices(obj: Record<string, unknown>): Record<string, number> | null {
  const result: Record<string, number> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'number') {
      result[key] = val;
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

function extractAccommodationOptions(prices: Record<string, unknown> | null): AccommodationOption[] {
  if (!prices || typeof prices !== 'object') { return []; }
  const raw = prices['accommodationOptions'];
  if (!Array.isArray(raw)) { return []; }

  return raw
    .filter((o): o is {
      id?: string;
      name?: string;
      type?: string;
      translations?: Record<string, string>;
      price?: number;
      currency?: string;
      prices?: Record<string, number>;
      baseCurrency?: string;
    } => typeof o === 'object' && o !== null)
    .map((o) => {
      // Support legacy format { type: 'single', price: 50, currency: 'USD' }
      if (o.type && !o.translations && !o.name) {
        const legacyName = o.type === 'single' ? 'Одноместное размещение' : 'Двухместное размещение';
        return {
          id: o.id ?? crypto.randomUUID(),
          translations: { ru: legacyName },
          prices: o.prices ?? (o.price !== undefined ? { [o.currency ?? 'UZS']: o.price } : {}),
          baseCurrency: o.baseCurrency ?? o.currency ?? 'UZS',
        };
      }

      // Support { name: 'xxx' } without translations
      if (o.name && !o.translations) {
        return {
          id: o.id ?? crypto.randomUUID(),
          translations: { ru: o.name },
          prices: o.prices ?? {},
          baseCurrency: o.baseCurrency ?? 'UZS',
        };
      }

      return {
        id: o.id ?? crypto.randomUUID(),
        translations: o.translations ?? {},
        prices: o.prices ?? {},
        baseCurrency: o.baseCurrency ?? 'UZS',
      };
    });
}
